import type { Dictionary } from "lodash";
import { z, type ZodInt, type ZodString, type ZodType } from "zod/v4";

/**
 * Creates a Zod schema for a non-negative integer, typically used for IDs.
 *
 * @returns {ZodInt} A Zod schema for a non-negative integer, typically used for IDs.
 */
export const id = (): ZodInt => z.int().min(0);

/**
 * Creates a Zod schema for a non-empty string, typically used for names or titles.
 *
 * @returns {ZodString} A Zod schema for a non-empty string
 */
export const name = (): ZodString => z.string().nonempty();

/**
 * An object that remembers it's original data.
 */
interface RestorableObject {
    __original: Dictionary<unknown>;
}

/**
 * Creates a Zod schema that can be restored to its raw form. This is useful for PUT endpoints so that we can
 * maintain original data, that might be unused by us, but still needs to be sent back as it was received.
 *
 * @param {ZodType<TInput>} input - The Zod schema for the input data.
 * @param {(data: TInput) => TOutput} fn - A function that maps the input data to the output format.
 * @returns {ZodType<TOutput & RestorableObject>} A Zod schema that includes the original data as `__original`.
 */
export const restorable = <TInput, TOutput extends object>(
    input: ZodType<TInput>,
    fn: (data: TInput) => TOutput,
): ZodType<TOutput & RestorableObject> =>
    input.transform((data) => ({
        ...fn(data),
        __original: data,
    })) as ZodType<TOutput & RestorableObject>;
